import { useNavigate } from "react-router-dom";
import { ExternalLink, LogOut, Settings2, ShieldCheck, Users } from "lucide-react";
import { PageHeader } from "@/components/catalog/TitleGrid";
import { InstallButton } from "@/components/layout/InstallButton";
import { useAuth } from "@/context/AuthContext";
import { useProfiles } from "@/context/ProfileContext";
import { useBilling } from "@/context/BillingContext";
import { BillingPanel } from "@/components/billing/BillingPanel";
import { BACKEND_URL } from "@/lib/api";
import { AdminTwoFactor, CreatorApplication } from "@/pages/AccountExtras";
import { useCreatorOn } from "@/lib/platform";

const Card = ({ title, children, testId }) => (
  <section data-testid={testId} className="panel p-6 sm:p-8">
    <h2 className="eyebrow mb-5">{title}</h2>
    {children}
  </section>
);

export default function Account() {
  const { user, logout } = useAuth();
  const creatorOn = useCreatorOn();
  const { activeProfile, profiles } = useProfiles();
  const navigate = useNavigate();
  const { ent } = useBilling();

  return (
    <div data-testid="account-page">
      <PageHeader eyebrow="Your membership" title="Account" />
      <div className="mx-auto grid max-w-[1680px] gap-6 px-4 sm:px-6 lg:px-10 lg:grid-cols-3">
        <Card title="Current profile" testId="account-profile-card">
          <div className="flex items-center gap-5">
            <img src={activeProfile?.avatar} alt="" className="h-20 w-20 rounded-2xl object-cover ring-1 ring-[#D4AF37]" />
            <div>
              <p data-testid="account-profile-name" className="font-display text-2xl text-white">{activeProfile?.name}</p>
              <p className="text-sm text-[#8B8B8B]">{activeProfile?.is_kids ? "Kids profile · mature titles hidden" : activeProfile?.is_owner ? "Owner profile" : "Standard profile"}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="account-switch-profile-btn" onClick={() => navigate("/profiles")} className="btn-ghost-gold px-5 py-2.5 text-sm"><Users className="h-4 w-4 text-[#D4AF37]" /> Switch</button>
            <button data-testid="account-manage-profiles-btn" onClick={() => navigate("/profiles?manage=1")} className="btn-ghost-gold px-5 py-2.5 text-sm"><Settings2 className="h-4 w-4 text-[#D4AF37]" /> Manage ({profiles?.length}/{ent?.max_profiles ?? 5})</button>
          </div>
        </Card>
        <Card title="Account details" testId="account-details-card">
          <dl className="space-y-4 text-sm">
            <div><dt className="text-[#8B8B8B]">Name</dt><dd className="text-white text-base">{user?.name}</dd></div>
            <div><dt className="text-[#8B8B8B]">Email</dt><dd data-testid="account-email" className="text-white text-base break-all">{user?.email}</dd></div>
            <div className="flex gap-6">
              <div><dt className="text-[#8B8B8B]">Sign-in</dt><dd className="text-white capitalize">{user?.auth_provider}</dd></div>
              <div><dt className="text-[#8B8B8B]">Role</dt><dd data-testid="account-role" className="text-[#FFD86A] capitalize">{user?.role}</dd></div>
            </div>
          </dl>
        </Card>
        <section id="billing" data-testid="account-billing-card" className="panel p-6 sm:p-8 lg:col-span-3 lg:order-first">
          <h2 className="eyebrow mb-5">Plan & billing</h2>
          <BillingPanel />
        </section>
        {creatorOn && !["admin", "owner"].includes(user?.role) && <CreatorApplication />}
        {user?.role === "admin" && <AdminTwoFactor />}
        <Card title="App & session" testId="account-app-card">
          <p className="text-sm text-[#8B8B8B] mb-5">Install AETRYX for a full-screen, app-like cinema on your desktop or phone.</p>
          <div className="flex flex-col gap-3">
            <InstallButton testId="account-install-btn" className="py-3" />
            {["admin", "owner"].includes(user?.role) && (
              <a data-testid="account-admin-api-link" href={`${BACKEND_URL}/api/docs`} target="_blank" rel="noreferrer" className="btn-ghost-gold py-3 text-sm">
                <ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Admin API (catalog CRUD) <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button data-testid="account-sign-out-btn" onClick={async () => { await logout(); navigate("/login"); }} className="flex items-center justify-center gap-2 rounded-full border border-[#E50914]/40 py-3 text-sm font-semibold text-[#ff6b6b] hover:bg-[#E50914]/10 transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
